import { useRef, useState } from 'react';

import { GooglePrediction } from '@rbilabs/components-library';

import { googlePredictionMapper } from 'components/autocomplete-address-input/googlePrediction.mapper';
import { useDebounceEffect } from 'hooks/use-debounce-effect';
import { useStoreContext } from 'state/store';

import { MapProviders } from './enum';
import { useAddressTomTomPredictions } from './use-address-tomtom-predictions';
import { IUseLocationPredictionsOptions, useLocationPredictions } from './use-location-predictions';

type TSharedArgs = Pick<IUseLocationPredictionsOptions, 'matchTypes' | 'skip'>;

interface IUseAddressPredictionsArgs extends TSharedArgs {
  pieceOfAddress: string;
}

export const useAddressPredictions = ({
  pieceOfAddress,
  matchTypes = [],
  skip = true,
}: IUseAddressPredictionsArgs) => {
  const { setAddressProvider } = useStoreContext();
  const { getPredictions: getTomTomPredictions } = useAddressTomTomPredictions();
  const googlePredictionsState = useLocationPredictions(pieceOfAddress, { matchTypes, skip });
  const [predictions, setPredictions] = useState<GooglePrediction[]>([]);
  const previousPieceOfAddress = useRef<string | undefined>(undefined);

  useDebounceEffect(
    () => {
      if (skip) {
        return;
      }

      const handleTomTom = async () => {
        // Prevent the request from being fired if the address is still the same between all the re-renders. This will prevent the current loop problem when Tomtom is enable
        if (pieceOfAddress === previousPieceOfAddress.current) {
          return;
        }
        previousPieceOfAddress.current = pieceOfAddress;
        const tomtomPredictions = await getTomTomPredictions(pieceOfAddress);

        const firstFivePredictions = tomtomPredictions.slice(0, 5)?.map(googlePredictionMapper);

        setPredictions(firstFivePredictions);
      };

      if (pieceOfAddress) {
        const googlePredictions =
          googlePredictionsState?.slice(0, 5)?.map(googlePredictionMapper) || [];

        if (!googlePredictions?.length) {
          handleTomTom();
          setAddressProvider(MapProviders.TOMTOM);
        } else {
          setAddressProvider(MapProviders.GOOGLE);
          setPredictions(googlePredictions);
        }
      }
    },
    400,
    [pieceOfAddress, googlePredictionsState, skip, getTomTomPredictions, setAddressProvider]
  );

  return { predictions };
};
